/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { differenceInCalendarMonths } from "date-fns";
import { IEvidence } from "@interfaces/Interfaces";
import { Icon_Tick_Green } from "@assets/images";
import SegmentedProgressBar from "@components/common/SegmentedProgressBar";
import * as ProgramUtils from "@utils/programUtils";
import { LEARNING_OUTCOMES_IDS } from "@constants/programs";

const EVIDENCE_REQURED_PER_MONTH = 2;

const MonthlyEvidenceBreakdown = (props: {
  evidence: IEvidence[];
  programId: string;
  generalCompId: string;
  paraPassLearningCompId: string;
}) => {
  const filteredEvidence = getFilteredEvidence();
  const months = getMonths();

  const groupedEvidence = filteredEvidence.reduce((acc: { [key: string]: IEvidence[] }, current) => {
    let d = new Date();
    if (
      current.evidenceJSON.programInfo?.progressCheckCompetenceID ===
      LEARNING_OUTCOMES_IDS.LAS_CA_ONGOING_DEVELOPMENT_LO
    ) {
      d = new Date(current.evidenceJSON["Date of Call"]);
    } else if (
      current.evidenceJSON.programInfo?.progressCheckCompetenceID === props.paraPassLearningCompId ||
      current.evidenceJSON.programInfo?.progressCheckCompetenceID === props.generalCompId
    ) {
      d = new Date(current.evidenceJSON["Date of activity"]);
    } else {
      d = new Date(current.date);
    }

    const month = d.toLocaleString("default", { month: "long", year: "numeric" });
    if (!acc[month]) {
      acc[month] = [];
    }
    acc[month].push(current);
    return acc;
  }, {});

  // Only get relevant evidence that should be counted for ongoing development
  // For CHUB: include 'Peer Review' comps with Peer Review LO and 'General' comps
  // For CA: include 'Peer Review' comps and 'General' comps
  function getFilteredEvidence() {
    return [...props.evidence].filter((e) => {
      const parsedEvidence = e.evidenceJSON;
      let peerReviewDone = false;
      if (ProgramUtils.isCHUB(props.programId)) {
        peerReviewDone = parsedEvidence?.LearningOutcomes?.find(
          (lo: any) => lo.ID === LEARNING_OUTCOMES_IDS.LAS_CHUB_ONGOING_DEVELOPMENT_LO // Peer review LO ID for LAS CHUB
        );
      } else if (ProgramUtils.isClinicalAdvisor(props.programId)) {
        peerReviewDone =
          parsedEvidence?.programInfo?.progressCheckCompetenceID ===
          LEARNING_OUTCOMES_IDS.LAS_CA_ONGOING_DEVELOPMENT_LO; // Peer Review comp ID for LAS CA
      }
      return (
        !parsedEvidence?.OnHoldReset &&
        !e.draft &&
        (parsedEvidence?.programInfo?.progressCheckCompetenceID == props.generalCompId ||
          parsedEvidence?.programInfo?.progressCheckCompetenceID == props.paraPassLearningCompId ||
          peerReviewDone)
      );
    });
  }

  function getMonths() {
    const months = [];
    const today = new Date();
    const oldestDateForEvidence = new Date(Math.min(...filteredEvidence.map((e) => new Date(e.date).getTime())));
    const monthsDiff = differenceInCalendarMonths(today, oldestDateForEvidence);
    const maxMonthBackdate = monthsDiff > 6 ? 6 : monthsDiff;

    for (let i = 0; i < maxMonthBackdate + 1; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      months.push(date.toLocaleString("default", { month: "long", year: "numeric" }));
    }

    return months;
  }

  function getEvidenceCountColour(count: number) {
    let colour = "text-grey-60";
    if (count > 0 && count < EVIDENCE_REQURED_PER_MONTH) {
      colour = "text-cta-red";
    } else if (count === EVIDENCE_REQURED_PER_MONTH) {
      colour = "text-green-highlight";
    }
    return colour;
  }

  return (
    <>
      {months.length ? (
        <>
          {months.map((month, index) => {
            const evidenceCount = groupedEvidence[month] ? groupedEvidence[month].length : 0;
            if (index === 0) {
              // show data for current month
              return (
                <SegmentedProgressBar
                  title="Evidence added this month"
                  key={index}
                  numerator={evidenceCount}
                  denominator={EVIDENCE_REQURED_PER_MONTH}
                  customTitleClasses="font-bold mb-[-8px]"
                />
              );
            } else {
              // show data for previous months
              const monthPart = month.split(" ").length > 0 ? month.split(" ")[0] : "";
              return (
                <div
                  key={index}
                  className={index === 1 ? "mt-1" : ""}
                >
                  <div className="flex justify-between items-center py-3">
                    <div className="text-acc-15px text-grey-90">
                      <>{monthPart}</>
                      {evidenceCount < EVIDENCE_REQURED_PER_MONTH && (
                        <div className="text-cta-red text-acc-12px font-medium">
                          You haven’t added enough evidence this month
                        </div>
                      )}
                    </div>
                    <div className="flex items-center">
                      {evidenceCount === EVIDENCE_REQURED_PER_MONTH && (
                        <img
                          src={Icon_Tick_Green}
                          className="mr-2 h-[18px] w-[18px]"
                        />
                      )}
                      <div className={`text-acc-15px font-bold ${getEvidenceCountColour(evidenceCount)}`}>
                        {evidenceCount} / {EVIDENCE_REQURED_PER_MONTH}
                      </div>
                    </div>
                  </div>
                  <div className="h-[1px] bg-grey-20" />
                </div>
              );
            }
          })}
        </>
      ) : (
        <SegmentedProgressBar
          title="Evidence added this month"
          numerator={0}
          denominator={EVIDENCE_REQURED_PER_MONTH}
          customTitleClasses="font-bold mb-[-8px]"
        />
      )}
    </>
  );
};

export default MonthlyEvidenceBreakdown;
